/**
 * Canonical Firebase initialization for the Endeeko web client.
 *
 * Exposes a single FirebaseApp instance + the Auth service. Because the same app
 * instance is shared with `src/services/api/firebase.ts` (Cloud Functions), the
 * signed-in user's ID token is automatically attached to every httpsCallable
 * request — so the endeeko backend guards (requireCompanyMember / requireSubscription)
 * work with zero extra wiring.
 *
 * Config comes from `VITE_FIREBASE_*` env vars — see .env.example and the setup
 * instructions in README / the assistant response.
 */
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, browserLocalPersistence, setPersistence, type Auth } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_MEASUREMENT_ID,
};

export function getFirebaseApp(): FirebaseApp {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export const app = getFirebaseApp();
export const auth: Auth = getAuth(app);

// Keep users signed in across reloads/tabs (this is the Firebase default, set
// explicitly so the intent is clear). Fire-and-forget; failures are non-fatal.
void setPersistence(auth, browserLocalPersistence).catch(() => {});

/**
 * App Check.
 *
 * The endeeko backend enforces App Check on its callables in production, so tokens
 * must ride along with every request. Enabled only when a reCAPTCHA v3 site key is
 * configured (`VITE_RECAPTCHA_SITE_KEY`) — otherwise skipped, so local/mock runs and
 * unconfigured environments work with zero setup.
 *
 * Local dev: set `VITE_APPCHECK_DEBUG_TOKEN` to a token registered in the Firebase
 * console, or leave it unset in dev to auto-generate one (printed to the console the
 * first time — register it under App Check → Debug tokens).
 */
// reCAPTCHA Enterprise site key (public — also loaded in index.html). Falls back
// to the registered key so a missing env var doesn't 401 every callable.
const recaptchaSiteKey =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string) || '6Lfm5R4tAAAAAOwDTw1WLPs32qqeG-oz5ZftYw8U';
export const appCheckEnabled = Boolean(recaptchaSiteKey);

if (typeof window !== 'undefined' && appCheckEnabled) {
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined;
  // Must be set before initializeAppCheck for the debug provider to be used.
  const w = self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean };
  // Dev builds only — a debug token baked into a production bundle makes every
  // visitor hit exchangeDebugToken (403) instead of the real reCAPTCHA provider.
  if (import.meta.env.DEV) w.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken || true;
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch {
    // Already initialized (e.g. HMR) or unsupported environment — non-fatal.
  }
}
